<template>
  <ehc-page class="d-5-md">
    <ehc-card :width="width" style="margin: 0 auto">
      <v-card-text class="text-h4 text-center pb-2">How-To Videos</v-card-text>

      <v-radio-group
        v-if="allVideos.length"
        v-model="audienceFilter"
        row
        hide-details
        class="px-3 pt-1 mb-1"
      >
        <v-radio label="All" value="all"></v-radio>
        <v-radio label="New Owners" value="New Owners"></v-radio>
        <v-radio label="Advanced" value="Advanced"></v-radio>
      </v-radio-group>

      <div
        v-if="allVideos.length"
        class="d-flex flex-wrap align-center px-3 mb-3"
      >
        <v-text-field
          v-model="searchQuery"
          class="how-to-search mr-3 mb-0"
          placeholder="Search titles, descriptions, or tags"
          outlined
          dense
          clearable
          hide-details
          prepend-inner-icon="mdi-magnify"
        ></v-text-field>
        <v-btn-toggle v-model="view" mandatory dense color="primary">
          <v-btn value="grid" small outlined>
            <v-icon small left>mdi-view-grid-outline</v-icon>
            Grid
          </v-btn>
          <v-btn value="list" small outlined>
            <v-icon small left>mdi-view-list-outline</v-icon>
            List
          </v-btn>
        </v-btn-toggle>
      </div>

      <v-row
        v-if="filteredVideos.length && view === 'grid'"
        align="stretch"
        class="mb-4 px-3"
      >
        <v-col
          v-for="video in filteredVideos"
          :key="video.id || video.title"
          cols="12"
          md="6"
          class="d-flex"
        >
          <v-card outlined class="how-to-card d-flex flex-column">
            <div class="how-to-card__copy">
              <div class="how-to-card__title">{{ video.title }}</div>
              <div class="how-to-card__description">
                {{ video.description }}
              </div>
              <div class="how-to-card__tags">
                <v-chip
                  v-for="tag in video.tags"
                  :key="tag"
                  x-small
                  outlined
                  class="mr-1 mb-1"
                  @click="searchQuery = tag"
                >
                  {{ tag }}
                </v-chip>
              </div>
            </div>
            <div class="support-video mt-auto">
              <iframe
                :src="video.embedUrl"
                :title="video.title"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
              ></iframe>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <div v-else-if="filteredVideos.length && view === 'list'" class="px-3 mb-4">
        <v-card
          v-for="video in filteredVideos"
          :key="video.id || video.title"
          outlined
          class="how-to-list-item mb-3"
        >
          <div class="how-to-list-item__inner">
            <a
              class="how-to-list-item__watch"
              :href="video.url || video.embedUrl"
              target="_blank"
              rel="noopener noreferrer"
              :aria-label="'Watch ' + video.title + ' on YouTube'"
            >
              <v-icon large color="red darken-1">mdi-youtube</v-icon>
              <span class="how-to-list-item__watch-label">Watch on YouTube</span>
            </a>
            <div class="how-to-list-item__copy">
              <div class="how-to-card__title">{{ video.title }}</div>
              <div class="how-to-list-item__description">
                {{ video.description }}
              </div>
              <div class="mt-2">
                <v-chip
                  v-for="tag in video.tags"
                  :key="tag"
                  x-small
                  outlined
                  class="mr-1 mb-1"
                  @click="searchQuery = tag"
                >
                  {{ tag }}
                </v-chip>
              </div>
            </div>
          </div>
        </v-card>
      </div>

      <v-card-text v-else class="text-center grey--text pb-8">
        {{ emptyMessage }}
      </v-card-text>
    </ehc-card>
  </ehc-page>
</template>

<script>
import EhcCard from "../components/support/ehc-card.vue";
import {
  embeddableHowToVideos,
  filterHowToVideos,
} from "@/utils/howToVideos";

export default {
  components: { EhcCard },
  data() {
    return {
      searchQuery: "",
      view: "grid",
    };
  },
  computed: {
    width() {
      if (window.innerWidth < 800) {
        return "700";
      }
      if (window.innerWidth < 1200) {
        return "1000";
      }
      return "1400";
    },
    allVideos() {
      return embeddableHowToVideos(this.$store.getters.appSettings || {});
    },
    audienceFilter: {
      get() {
        const q = String(this.searchQuery || "")
          .trim()
          .toLowerCase();
        if (!q) {
          return "all";
        }
        if (q === "new owners") {
          return "New Owners";
        }
        if (q === "advanced") {
          return "Advanced";
        }
        return "custom";
      },
      set(value) {
        if (!value || value === "all" || value === "custom") {
          this.searchQuery = "";
          return;
        }
        this.searchQuery = value;
      },
    },
    filteredVideos() {
      return filterHowToVideos(this.allVideos, this.searchQuery);
    },
    emptyMessage() {
      if (!this.allVideos.length) {
        return "How-to videos will appear here once they are added in Settings.";
      }
      return "No how-to videos match that search.";
    },
  },
};
</script>

<style scoped>
.how-to-search {
  flex: 1 1 240px;
  max-width: 420px;
}
.how-to-card {
  width: 100%;
  height: 100%;
}
.how-to-card__copy {
  display: grid;
  grid-template-rows: 1.4em 2.8em 28px;
  row-gap: 4px;
  padding: 16px 16px 12px;
}
.how-to-card__title {
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.4;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  word-break: break-word;
}
.how-to-card__description {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.875rem;
  line-height: 1.4;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  word-break: break-word;
}
.how-to-card__tags {
  overflow: hidden;
  white-space: nowrap;
}
.how-to-list-item__inner {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
}
.how-to-list-item__watch {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 0 0 88px;
  text-decoration: none;
  color: inherit;
}
.how-to-list-item__watch-label {
  margin-top: 4px;
  font-size: 0.7rem;
  line-height: 1.2;
  text-align: center;
  color: rgba(0, 0, 0, 0.6);
}
.how-to-list-item__copy {
  min-width: 0;
  flex: 1 1 auto;
  padding: 4px 0;
}
.how-to-list-item__description {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.875rem;
  line-height: 1.4;
  margin-top: 4px;
}
.support-video {
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
  width: 100%;
}
.support-video iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
@media (max-width: 799px) {
  .how-to-list-item__inner {
    align-items: flex-start;
  }
}
</style>
